import {
  Button,
  Card,
  CopyButton,
  Group,
  NumberInput,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { useMutation } from '@tanstack/react-query'
import { type JSX, useState } from 'react'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'

type GeneratedPatient = {
  id: number
  first_name: string
  last_name: string
}

export default function DemoPatientGenerator(): JSX.Element {
  const [patientsToGenerate, setPatientsToGenerate] = useState(1)

  const [generatedPatients, setGeneratedPatients] = useState<
    GeneratedPatient[]
  >([])
  const { seenApi } = useSeenApi()
  const mutation = useMutation<GeneratedPatient[], Error, number>({
    mutationFn: async (n) => {
      const res = await seenApi.post<{ patients: GeneratedPatient[] }>(
        '/demo-patient-generation',
        { numberToGenerate: n }
      )
      return res.data.patients
    },
    onSuccess: (data) => {
      setGeneratedPatients(data)
    },
  })

  return (
    <Stack>
      <Title size="h3">Demo Patient Generator</Title>
      <NumberInput
        value={patientsToGenerate}
        onChange={(e) => {
          setPatientsToGenerate(Number(e))
        }}
        allowDecimal={false}
        min={1}
        max={10}
      />
      <Button
        onClick={() => mutation.mutate(patientsToGenerate)}
        loading={mutation.isPending}
      >
        Generate {patientsToGenerate} Demo Patient
        {patientsToGenerate > 1 ? 's' : ''}
      </Button>
      {generatedPatients?.length > 0 && (
        <Stack>
          <Text>Successfully generated the following patients:</Text>
          {generatedPatients.map((patient, index) => (
            <Card shadow="sm" padding="lg" radius="md" withBorder key={index}>
              <Group justify="space-between" gap="sm">
                <Text fw={500}>
                  {patient.first_name} {patient.last_name}
                </Text>
                <CopyButton value={String(patient.id)}>
                  {({ copied, copy }) => (
                    <Button
                      size="compact-sm"
                      color={copied ? 'teal' : 'gray'}
                      onClick={copy}
                    >
                      {copied ? 'Copied ID' : patient.id}
                    </Button>
                  )}
                </CopyButton>
              </Group>

              <Button
                component="a"
                href={`https://sandbox.elationemr.com/patient/${patient.id}/`}
                target="_blank"
                color="blue"
                size="sm"
                mt="md"
                radius="md"
              >
                Open in Elation
              </Button>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  )
}
